import { useState, useEffect } from "react";
import { api } from "../utils/api";
import {
  TrendingUp, ShoppingBag, Bike, Users, UtensilsCrossed,
  ShoppingBasket, Package, ArrowUpRight, ArrowDownRight,
  Clock, CheckCircle2, XCircle, Loader2, IndianRupee, RefreshCw,
  Activity, Zap,
} from "lucide-react";

// ── Design Tokens ─────────────────────────────────
const T = {
  primary:"#f97316", primaryDark:"#c2410c", primaryLight:"#fff7ed",
  white:"#ffffff", bg:"#f8fafc", card:"#ffffff",
  text:"#0c0a09", textSub:"#57534e", textMuted:"#a8a29e",
  border:"#e7e5e4", borderLight:"#f5f5f4",
  green:"#16a34a", greenLight:"#f0fdf4", greenDark:"#15803d",
  red:"#dc2626",   redLight:"#fef2f2",
  yellow:"#d97706", yellowLight:"#fffbeb",
  blue:"#2563eb",  blueLight:"#eff6ff",
  purple:"#7c3aed",purpleLight:"#faf5ff",
  shadow:"0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  shadowMd:"0 4px 16px rgba(0,0,0,0.08)",
  shadowLg:"0 8px 32px rgba(0,0,0,0.10)",
};

const STATUS_MAP = {
  placed:    { label:"Placed",     bg:"#eff6ff", color:"#1d4ed8", icon:Clock         },
  accepted:  { label:"Accepted",   bg:"#fffbeb", color:"#b45309", icon:CheckCircle2  },
  preparing: { label:"Preparing",  bg:"#fff7ed", color:"#c2410c", icon:Loader2       },
  packing:   { label:"Packing",    bg:"#faf5ff", color:"#6d28d9", icon:Package       },
  packed:    { label:"Packed",     bg:"#f0fdf4", color:"#15803d", icon:Package       },
  ready:     { label:"Ready",      bg:"#ecfdf5", color:"#047857", icon:CheckCircle2  },
  picked_up: { label:"Picked Up",  bg:"#faf5ff", color:"#7c3aed", icon:Bike          },
  "on-the-way":{ label:"On Way",   bg:"#eff6ff", color:"#1d4ed8", icon:Bike          },
  delivered: { label:"Delivered",  bg:"#f0fdf4", color:"#15803d", icon:CheckCircle2  },
  cancelled: { label:"Cancelled",  bg:"#fef2f2", color:"#b91c1c", icon:XCircle       },
};

// ── Skeleton loader ───────────────────────────────
const Skeleton = ({ w = "100%", h = 20, r = 8 }) => (
  <div style={{ width:w, height:h, borderRadius:r, background:"linear-gradient(90deg,#f5f5f4 25%,#e7e5e4 50%,#f5f5f4 75%)", backgroundSize:"200% 100%", animation:"shimmer 1.4s infinite" }}/>
);

// ── Stat Card ─────────────────────────────────────
function StatCard({ icon:Icon, label, value, sub, color, colorLight, trend, trendUp, loading, delay=0 }) {
  return (
    <div style={{
      background:T.card, borderRadius:16, border:`1px solid ${T.border}`,
      padding:"22px 20px", display:"flex", flexDirection:"column", gap:16,
      boxShadow:T.shadow, animation:`fadeUp .5s ease both`, animationDelay:`${delay}s`,
      transition:"box-shadow .2s, transform .2s", cursor:"default",
    }}
      onMouseEnter={e => { e.currentTarget.style.boxShadow = T.shadowMd; e.currentTarget.style.transform = "translateY(-2px)"; }}
      onMouseLeave={e => { e.currentTarget.style.boxShadow = T.shadow;   e.currentTarget.style.transform = "translateY(0)"; }}
    >
      <div style={{ display:"flex", alignItems:"flex-start", justifyContent:"space-between" }}>
        <div style={{ width:46, height:46, borderRadius:13, background:colorLight, display:"flex", alignItems:"center", justifyContent:"center", border:`1px solid ${color}22` }}>
          <Icon size={21} color={color} strokeWidth={1.8}/>
        </div>
        {trend && (
          <div style={{ display:"flex", alignItems:"center", gap:3, background:trendUp?T.greenLight:T.redLight, color:trendUp?T.greenDark:T.red, padding:"4px 10px", borderRadius:100, fontSize:"0.7rem", fontWeight:700, border:`1px solid ${trendUp?"#bbf7d0":"#fecaca"}` }}>
            {trendUp ? <ArrowUpRight size={12}/> : <ArrowDownRight size={12}/>}{trend}
          </div>
        )}
      </div>
      <div>
        {loading
          ? <><Skeleton h={32} w="60%" r={6}/><Skeleton h={14} w="80%" r={4} style={{marginTop:8}}/></>
          : <>
              <div style={{ fontFamily:"'DM Serif Display',Georgia,serif", fontWeight:400, fontSize:"2rem", letterSpacing:"-1px", color:T.text, lineHeight:1 }}>{value}</div>
              <div style={{ fontWeight:600, fontSize:"0.8rem", color:T.textSub, marginTop:5 }}>{label}</div>
              {sub && <div style={{ fontSize:"0.7rem", color:T.textMuted, marginTop:3 }}>{sub}</div>}
            </>
        }
      </div>
    </div>
  );
}

// ── Mini Badge ────────────────────────────────────
function MiniStat({ label, val, color, bg, loading }) {
  return (
    <div style={{ background:bg, borderRadius:14, padding:"16px 18px", border:`1px solid ${color}22`, flex:1 }}>
      {loading
        ? <Skeleton h={28} w="50%" r={6}/>
        : <div style={{ fontFamily:"'DM Serif Display',Georgia,serif", fontWeight:400, fontSize:"1.7rem", color, lineHeight:1 }}>{val}</div>
      }
      <div style={{ fontSize:"0.7rem", fontWeight:600, color:T.textSub, marginTop:5 }}>{label}</div>
    </div>
  );
}

// ── Bar Chart ─────────────────────────────────────
function BarChart({ chart, loading }) {
  const max = Math.max(...chart.map(d => d.orders || 0), 1);
  const days = chart.length ? chart : Array(7).fill(0).map((_,i) => ({
    date: ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"][i],
    orders: 0, revenue: 0,
  }));

  return (
    <div style={{ background:T.card, borderRadius:16, border:`1px solid ${T.border}`, padding:"22px 24px", boxShadow:T.shadow, animation:"fadeUp .5s ease both", animationDelay:".2s" }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24 }}>
        <div>
          <div style={{ fontWeight:700, fontSize:"0.95rem", color:T.text }}>Order Trend</div>
          <div style={{ fontSize:"0.72rem", color:T.textMuted, marginTop:2 }}>Last 7 days</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:6, background:T.primaryLight, padding:"5px 12px", borderRadius:100, fontSize:"0.72rem", fontWeight:700, color:T.primary }}>
          <Activity size={12}/> Live
        </div>
      </div>
      <div style={{ display:"flex", alignItems:"flex-end", gap:6, height:140 }}>
        {days.map((d, i) => {
          const h = loading ? 40 : Math.max(6, ((d.orders||0) / max) * 120);
          return (
            <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:6, cursor:"default" }}
              title={`${d.date}: ${d.orders} orders`}>
              <div style={{ fontSize:"0.6rem", fontWeight:700, color:loading?T.borderLight:T.textMuted }}>
                {loading ? "" : d.orders||""}
              </div>
              <div style={{
                width:"100%", height:h,
                background: loading
                  ? "linear-gradient(180deg,#f5f5f4,#e7e5e4)"
                  : `linear-gradient(180deg,${T.primary},${T.primaryDark})`,
                borderRadius:"6px 6px 0 0",
                transition:"height .8s cubic-bezier(.34,1.56,.64,1)",
                animation: loading ? "shimmer 1.4s infinite" : "none",
              }}/>
              <div style={{ fontSize:"0.62rem", color:T.textMuted, fontWeight:600 }}>{d.date}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Service Split ─────────────────────────────────
function ServiceSplit({ s, loading }) {
  const services = [
    { label:"Food Delivery", pct:58, color:T.primary, Icon:UtensilsCrossed },
    { label:"Grocery",       pct:27, color:T.green,   Icon:ShoppingBasket  },
    { label:"Courier",       pct:15, color:T.blue,    Icon:Package         },
  ];
  return (
    <div style={{ background:T.card, borderRadius:16, border:`1px solid ${T.border}`, padding:"22px 24px", boxShadow:T.shadow, animation:"fadeUp .5s ease both", animationDelay:".25s" }}>
      <div style={{ fontWeight:700, fontSize:"0.95rem", color:T.text, marginBottom:4 }}>Service Split</div>
      <div style={{ fontSize:"0.72rem", color:T.textMuted, marginBottom:20 }}>Order distribution</div>
      {services.map((sv, i) => (
        <div key={i} style={{ marginBottom:i < services.length-1 ? 18 : 0 }}>
          <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:8 }}>
            <div style={{ display:"flex", alignItems:"center", gap:8 }}>
              <div style={{ width:30, height:30, borderRadius:9, background:`${sv.color}14`, display:"flex", alignItems:"center", justifyContent:"center", border:`1px solid ${sv.color}22` }}>
                <sv.Icon size={13} color={sv.color} strokeWidth={1.8}/>
              </div>
              <span style={{ fontWeight:600, fontSize:"0.8rem", color:T.text }}>{sv.label}</span>
            </div>
            <span style={{ fontWeight:800, fontSize:"0.82rem", color:sv.color }}>{sv.pct}%</span>
          </div>
          <div style={{ height:6, background:T.borderLight, borderRadius:100, overflow:"hidden" }}>
            <div style={{ height:"100%", width:loading?0:`${sv.pct}%`, background:`linear-gradient(90deg,${sv.color}99,${sv.color})`, borderRadius:100, transition:"width 1.2s cubic-bezier(.34,1.56,.64,1)" }}/>
          </div>
        </div>
      ))}

      {/* Live Now */}
      <div style={{ marginTop:20, padding:"14px 16px", background:T.bg, borderRadius:12, border:`1px solid ${T.border}` }}>
        <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:12 }}>
          <div style={{ width:7, height:7, borderRadius:"50%", background:T.green, animation:"pulse 1.5s infinite" }}/>
          <span style={{ fontSize:"0.65rem", fontWeight:700, color:T.textMuted, letterSpacing:"1.5px", textTransform:"uppercase" }}>Live Now</span>
        </div>
        {[
          { label:"Orders in progress", val:s.pendingOrders??0,  color:T.primary },
          { label:"Riders on road",     val:s.onlineRiders??0,   color:T.green   },
          { label:"Awaiting acceptance",val:s.pendingOrders??0,  color:T.yellow  },
        ].map((l, i) => (
          <div key={i} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:i<2?9:0 }}>
            <span style={{ fontSize:"0.75rem", color:T.textSub }}>{l.label}</span>
            {loading
              ? <Skeleton w={28} h={16} r={4}/>
              : <span style={{ fontWeight:800, fontSize:"0.88rem", color:l.color }}>{l.val}</span>
            }
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Recent Orders Table ───────────────────────────
function RecentOrders({ orders, loading }) {
  return (
    <div style={{ background:T.card, borderRadius:16, border:`1px solid ${T.border}`, overflow:"hidden", boxShadow:T.shadow, animation:"fadeUp .5s ease both", animationDelay:".3s" }}>
      <div style={{ padding:"18px 24px", borderBottom:`1px solid ${T.border}`, display:"flex", alignItems:"center", justifyContent:"space-between" }}>
        <div>
          <div style={{ fontWeight:700, fontSize:"0.95rem", color:T.text }}>Recent Orders</div>
          <div style={{ fontSize:"0.7rem", color:T.textMuted, marginTop:2 }}>Latest activity</div>
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:5, fontSize:"0.7rem", color:T.textMuted }}>
          <Zap size={12} color={T.primary}/> Real-time
        </div>
      </div>

      {loading ? (
        <div style={{ padding:"24px 24px" }}>
          {Array(5).fill(0).map((_,i) => (
            <div key={i} style={{ display:"flex", gap:16, marginBottom:16, alignItems:"center" }}>
              <Skeleton w={60} h={14} r={4}/>
              <Skeleton w={100} h={14} r={4}/>
              <Skeleton w={120} h={14} r={4}/>
              <Skeleton w={50} h={14} r={4}/>
              <Skeleton w={70} h={22} r={100}/>
            </div>
          ))}
        </div>
      ) : orders.length === 0 ? (
        <div style={{ padding:60, textAlign:"center" }}>
          <ShoppingBag size={36} color={T.borderLight} style={{ margin:"0 auto 12px" }}/>
          <div style={{ fontSize:"0.85rem", color:T.textMuted, fontWeight:600 }}>No orders yet</div>
        </div>
      ) : (
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", minWidth:560 }}>
            <thead>
              <tr style={{ background:T.bg, borderBottom:`1px solid ${T.border}` }}>
                {["Order ID","Customer","Restaurant / Store","Amount","Status","Time"].map(h => (
                  <th key={h} style={{ padding:"10px 20px", textAlign:"left", fontSize:"0.62rem", fontWeight:700, color:T.textMuted, textTransform:"uppercase", letterSpacing:"0.8px", whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {orders.map((o, i) => {
                const st = STATUS_MAP[o.status] || STATUS_MAP.placed;
                const SIcon = st.icon;
                const storeName = o.restaurant?.name || o.groceryStore?.name || "—";
                return (
                  <tr key={o._id}
                    style={{ borderBottom:`1px solid ${T.borderLight}`, transition:"background .15s", animationDelay:`${i * 0.04}s` }}
                    onMouseEnter={e => e.currentTarget.style.background = T.bg}
                    onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding:"13px 20px" }}>
                      <span style={{ fontWeight:700, fontSize:"0.78rem", color:T.primary, fontFamily:"'DM Mono',monospace" }}>
                        #{o.orderId?.slice(-6) || o._id?.slice(-6)}
                      </span>
                    </td>
                    <td style={{ padding:"13px 20px", fontSize:"0.82rem", fontWeight:600, color:T.text }}>{o.user?.name || "—"}</td>
                    <td style={{ padding:"13px 20px", fontSize:"0.78rem", color:T.textSub }}>{storeName}</td>
                    <td style={{ padding:"13px 20px" }}>
                      <span style={{ fontWeight:800, fontSize:"0.85rem", color:T.text, fontFamily:"'DM Serif Display',Georgia,serif" }}>₹{o.total}</span>
                    </td>
                    <td style={{ padding:"13px 20px" }}>
                      <span style={{ display:"inline-flex", alignItems:"center", gap:5, background:st.bg, color:st.color, fontSize:"0.68rem", fontWeight:700, padding:"4px 10px", borderRadius:100, border:`1px solid ${st.color}22` }}>
                        <SIcon size={10}/>{st.label}
                      </span>
                    </td>
                    <td style={{ padding:"13px 20px", fontSize:"0.72rem", color:T.textMuted, fontFamily:"'DM Mono',monospace" }}>
                      {new Date(o.createdAt).toLocaleTimeString("en-IN", { hour:"2-digit", minute:"2-digit" })}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Main Dashboard ────────────────────────────────
export default function Dashboard() {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [period,  setPeriod]  = useState("today");
  const [error,   setError]   = useState("");
  const [now,     setNow]     = useState(new Date());

  useEffect(() => { const t = setInterval(() => setNow(new Date()), 60000); return () => clearInterval(t); }, []);

  const load = async () => {
    setLoading(true); setError("");
    try {
      const res = await api.dashboard();
      if (res.success) setData(res.data);
      else setError("Failed to load dashboard");
    } catch { setError("Backend se connect nahi ho pa raha"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const s            = data?.stats       || {};
  const recentOrders = data?.recentOrders || [];
  const chart        = data?.chart        || [];

  const fmt  = (n) => n >= 100000 ? `₹${(n/100000).toFixed(1)}L` : n >= 1000 ? `₹${(n/1000).toFixed(1)}k` : `₹${n||0}`;
  const hour = now.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  return (
    <div style={{ fontFamily:"'DM Sans','Plus Jakarta Sans',sans-serif", minHeight:"100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display&family=DM+Sans:wght@400;500;600;700;800&family=DM+Mono:wght@500&display=swap');
        @keyframes fadeUp { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
        @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
        @keyframes spin { to{transform:rotate(360deg)} }
        @keyframes pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
        .spin { animation:spin 1s linear infinite }
      `}</style>

      {/* ── Top Header ── */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:24, flexWrap:"wrap", gap:12, animation:"fadeUp .4s ease both" }}>
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ fontFamily:"'DM Serif Display',Georgia,serif", fontWeight:400, fontSize:"1.5rem", color:T.text, letterSpacing:"-0.5px" }}>
              {greeting}, Admin 👋
            </div>
          </div>
          <div style={{ fontSize:"0.75rem", color:T.textMuted, marginTop:3, display:"flex", alignItems:"center", gap:6 }}>
            <div style={{ width:6, height:6, borderRadius:"50%", background:T.green, animation:"pulse 1.5s infinite" }}/>
            {now.toLocaleDateString("en-IN", { weekday:"long", day:"numeric", month:"long" })} · Akola
          </div>
        </div>

        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          {/* Period selector */}
          <div style={{ display:"flex", background:T.card, border:`1px solid ${T.border}`, borderRadius:12, padding:4, gap:2 }}>
            {["today","week","month"].map(p => (
              <button key={p} onClick={() => setPeriod(p)} style={{
                background: period===p ? T.primary : "none",
                color:      period===p ? "#fff"    : T.textSub,
                border:"none", borderRadius:9, padding:"6px 16px",
                fontWeight:600, fontSize:"0.75rem", cursor:"pointer",
                textTransform:"capitalize", fontFamily:"inherit", transition:"all .2s",
              }}>{p}</button>
            ))}
          </div>

          {/* Refresh */}
          <button onClick={load} style={{
            background:T.card, border:`1px solid ${T.border}`, borderRadius:12,
            padding:"8px 16px", cursor:"pointer", display:"flex", alignItems:"center",
            gap:7, fontSize:"0.75rem", fontWeight:600, color:T.textSub,
            fontFamily:"inherit", transition:"all .2s", boxShadow:T.shadow,
          }}
            onMouseEnter={e => { e.currentTarget.style.borderColor=T.primary; e.currentTarget.style.color=T.primary; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor=T.border;  e.currentTarget.style.color=T.textSub; }}
          >
            <RefreshCw size={13} className={loading ? "spin" : ""}/> Refresh
          </button>
        </div>
      </div>

      {/* ── Error Banner ── */}
      {error && (
        <div style={{ background:"#fef2f2", border:"1px solid #fecaca", borderRadius:12, padding:"12px 18px", marginBottom:18, fontSize:"0.8rem", color:T.red, fontWeight:600, display:"flex", alignItems:"center", gap:8 }}>
          <XCircle size={15}/> {error}
        </div>
      )}

      {/* ── Stat Cards ── */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit,minmax(210px,1fr))", gap:14, marginBottom:20 }}>
        <StatCard icon={IndianRupee} label="Revenue"        value={fmt(s.monthRevenue||0)}   sub="This month · delivered"         color={T.primary} colorLight={T.primaryLight} trend="+18%" trendUp loading={loading} delay={0}/>
        <StatCard icon={ShoppingBag} label="Today's Orders" value={s.todayOrders??0}         sub={`${s.totalOrders??0} all time`} color={T.blue}    colorLight={T.blueLight}   trend="+12%" trendUp loading={loading} delay={.06}/>
        <StatCard icon={Bike}        label="Online Riders"  value={s.onlineRiders??0}        sub={`of ${s.totalRiders??0} total`} color={T.green}   colorLight={T.greenLight}  trend="Live" trendUp loading={loading} delay={.12}/>
        <StatCard icon={Users}       label="Customers"      value={s.totalUsers??0}          sub={`+${s.newUsersToday??0} today`} color={T.purple}  colorLight={T.purpleLight} trend="+42"  trendUp loading={loading} delay={.18}/>
      </div>

      {/* ── Mini Stats ── */}
      <div style={{ display:"flex", gap:12, marginBottom:20, flexWrap:"wrap", animation:"fadeUp .5s ease both", animationDelay:".18s" }}>
        <MiniStat label="Pending Orders"  val={loading?"—":s.pendingOrders??0}   color={T.yellow} bg={T.yellowLight} loading={loading}/>
        <MiniStat label="Delivered Today" val={loading?"—":s.deliveredOrders??0} color={T.green}  bg={T.greenLight}  loading={loading}/>
        <MiniStat label="Cancelled"       val={loading?"—":s.cancelledOrders??0} color={T.red}    bg={T.redLight}    loading={loading}/>
        <MiniStat label="Restaurants"     val={loading?"—":s.activeRestaurants??0} color={T.primary} bg={T.primaryLight} loading={loading}/>
      </div>

      {/* ── Chart + Service Split ── */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 300px", gap:14, marginBottom:20 }}>
        <BarChart chart={chart} loading={loading}/>
        <ServiceSplit s={s} loading={loading}/>
      </div>

      {/* ── Recent Orders ── */}
      <RecentOrders orders={recentOrders} loading={loading}/>
    </div>
  );
}